// 학기/근무자 관리 페이지 (관리자 전용)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Heading,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Switch,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Select,
  Checkbox,
  CheckboxGroup,
  Wrap,
  WrapItem
} from '@chakra-ui/react';
import { DeleteIcon, AddIcon, EditIcon } from '@chakra-ui/icons';
import { FaUserFriends } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import {
  getAllSemesters,
  createSemester,
  updateSemester,
  deleteSemester,
  getLocations
} from 'utils/firestoreUtils';

function SemesterManagement() {
  const toast = useToast();
  const history = useHistory();
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [semesters, setSemesters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSemester, setEditingSemester] = useState(null);

  const [formData, setFormData] = useState({
    semesterName: '',
    startDate: '',
    endDate: '',
    registrationStartDate: '',
    registrationEndDate: '',
    workplaces: [],
    defaultMaxWorkHours: 40,
    isActive: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await loadSemesters();
    await loadLocations();
  };

  const loadLocations = async () => {
    try {
      const locs = await getLocations();
      setLocations(locs);
    } catch (error) {
      console.error('근무지 조회 오류:', error);
    }
  };

  const loadSemesters = async () => {
    try {
      setLoading(true);
      const data = await getAllSemesters();
      setSemesters(data);
    } catch (error) {
      console.error('학기 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '학기 목록을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingSemester(null);
    setFormData({
      semesterName: '',
      startDate: '',
      endDate: '',
      registrationStartDate: '',
      registrationEndDate: '',
      workplaces: [],
      defaultMaxWorkHours: 40,
      isActive: false
    });
    onOpen();
  };

  const handleEdit = (semester) => {
    setEditingSemester(semester);
    setFormData({
      semesterName: semester.semesterName,
      startDate: semester.startDate,
      endDate: semester.endDate,
      registrationStartDate: semester.registrationStartDate,
      registrationEndDate: semester.registrationEndDate,
      workplaces: semester.workplaces || [],
      defaultMaxWorkHours: semester.defaultMaxWorkHours || 40,
      isActive: semester.isActive
    });
    onOpen();
  };

  const handleGoToDetail = (semester) => {
    history.push(`/admin/semester-management/${semester.id}`, { semester });
  };

  const handleSave = async () => {
    if (!formData.semesterName || !formData.startDate || !formData.endDate ||
        !formData.registrationStartDate || !formData.registrationEndDate) {
      toast({
        title: '입력 오류',
        description: '모든 필수 항목을 입력해주세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    // 날짜 유효성 검사
    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      toast({
        title: '입력 오류',
        description: '학기 시작일이 종료일보다 늦을 수 없습니다.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    if (new Date(formData.registrationStartDate) > new Date(formData.registrationEndDate)) {
      toast({
        title: '입력 오류',
        description: '입력 시작일이 종료일보다 늦을 수 없습니다.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    try {
      setLoading(true);
      
      if (editingSemester) {
        await updateSemester(editingSemester.id, formData);
        toast({
          title: '수정 완료',
          description: '학기 정보가 수정되었습니다.',
          status: 'success',
          duration: 3000
        });
      } else {
        await createSemester(formData);
        toast({
          title: '등록 완료',
          description: '학기가 등록되었습니다.',
          status: 'success',
          duration: 3000
        });
      }
      
      await loadSemesters();
      onClose();
    } catch (error) {
      console.error('저장 오류:', error);
      toast({
        title: '저장 실패',
        description: error.message || '학기 저장에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (semesterId) => {
    if (!window.confirm('정말 삭제하시겠습니까? 이미 등록된 학업시간표는 영향을 받지 않습니다.')) return;

    try {
      setLoading(true);
      await deleteSemester(semesterId);
      await loadSemesters();
      
      toast({
        title: '삭제 완료',
        description: '학기가 삭제되었습니다.',
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('삭제 오류:', error);
      toast({
        title: '삭제 실패',
        description: '학기 삭제에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (semester) => {
    try {
      setLoading(true);
      await updateSemester(semester.id, { isActive: !semester.isActive });
      await loadSemesters();
      
      toast({
        title: '변경 완료',
        description: `학기가 ${!semester.isActive ? '활성화' : '비활성화'}되었습니다.`,
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('변경 오류:', error);
      toast({
        title: '변경 실패',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const isRegistrationPeriod = (semester) => {
    const today = new Date();
    const startDate = new Date(semester.registrationStartDate);
    const endDate = new Date(semester.registrationEndDate);
    return today >= startDate && today <= endDate;
  };

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 영역 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={textColor} mb={1}>
            학기 / 근무자 관리
          </Heading>
          <Text fontSize="sm" color="gray.500">
            전체 {semesters.length}개 학기 | 활성 {semesters.filter(s => s.isActive).length}개
          </Text>
        </Box>
        <Button
          colorScheme="blue"
          leftIcon={<AddIcon />}
          onClick={handleNew}
          isLoading={loading}
        >
          학기 추가
        </Button>
      </Flex>

      {/* 메인 컨텐츠 */}
      <Card overflowX={{ sm: 'scroll', xl: 'hidden' }}>
        <CardBody>
          {semesters.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text color="gray.500">등록된 학기가 없습니다.</Text>
            </Box>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>학기명</Th>
                  <Th>학기 기간</Th>
                  <Th>시간표 입력 가능 기간</Th>
                  <Th>근무지</Th>
                  <Th>최대 근로시간</Th>
                  <Th>상태</Th>
                  <Th>활성</Th>
                  <Th>작업</Th>
                </Tr>
              </Thead>
              <Tbody>
                {semesters.map((semester) => (
                  <Tr key={semester.id}>
                    <Td fontWeight="bold">{semester.semesterName}</Td>
                    <Td fontSize="sm">
                      {semester.startDate} ~ {semester.endDate}
                    </Td>
                    <Td fontSize="sm">
                      <Text>{semester.registrationStartDate} ~ {semester.registrationEndDate}</Text>
                      {isRegistrationPeriod(semester) && (
                        <Badge colorScheme="green" mt={1}>입력 가능</Badge>
                      )}
                    </Td>
                    <Td fontSize="sm">
                      {semester.workplaces && semester.workplaces.length > 0 ? (
                        <Wrap>
                          {semester.workplaces.map((wp, idx) => (
                            <WrapItem key={idx}>
                              <Badge>{wp}</Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      ) : (
                        <Text color="gray.400">미설정</Text>
                      )}
                    </Td>
                    <Td fontSize="sm">
                      {semester.defaultMaxWorkHours ? `${semester.defaultMaxWorkHours}시간` : '-'}
                    </Td>
                    <Td>
                      {semester.isActive && <Badge colorScheme="green">현재 학기</Badge>}
                    </Td>
                    <Td>
                      <Switch
                        isChecked={semester.isActive}
                        onChange={() => handleToggleActive(semester)}
                        colorScheme="green"
                      />
                    </Td>
                    <Td>
                      <Flex gap={2}>
                        <IconButton
                          icon={<FaUserFriends />}
                          size="sm"
                          colorScheme="purple"
                          onClick={() => handleGoToDetail(semester)}
                          aria-label="근무자 관리"
                          title="근무자 관리"
                        />
                        <IconButton
                          icon={<EditIcon />}
                          size="sm"
                          onClick={() => handleEdit(semester)}
                          aria-label="수정"
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleDelete(semester.id)}
                          aria-label="삭제"
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* 학기 추가/수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingSemester ? '학기 수정' : '새 학기 추가'}
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>학기명</FormLabel>
                <Input
                  value={formData.semesterName}
                  onChange={(e) => setFormData({ ...formData, semesterName: e.target.value })}
                  placeholder="예: 2026-1학기"
                />
              </FormControl>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <FormControl isRequired>
                  <FormLabel>학기 시작일</FormLabel>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>학기 종료일</FormLabel>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  />
                </FormControl>
              </Grid>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <FormControl isRequired>
                  <FormLabel>시간표 입력 시작일</FormLabel>
                  <Input
                    type="date"
                    value={formData.registrationStartDate}
                    onChange={(e) => setFormData({ ...formData, registrationStartDate: e.target.value })}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>시간표 입력 종료일</FormLabel>
                  <Input
                    type="date"
                    value={formData.registrationEndDate}
                    onChange={(e) => setFormData({ ...formData, registrationEndDate: e.target.value })}
                  />
                </FormControl>
              </Grid>

              <FormControl>
                <FormLabel>근무지 선택</FormLabel>
                <CheckboxGroup 
                  value={formData.workplaces}
                  onChange={(values) => setFormData({ ...formData, workplaces: values })}
                >
                  <Wrap>
                    {locations.map((loc) => (
                      <WrapItem key={loc}>
                        <Checkbox value={loc}>{loc}</Checkbox>
                      </WrapItem>
                    ))}
                  </Wrap>
                </CheckboxGroup>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  이 학기에 활용할 근무지를 선택하세요.
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>기본 최대 근로시간 (시간 단위)</FormLabel>
                <Input
                  type="number"
                  min="1"
                  value={formData.defaultMaxWorkHours}
                  onChange={(e) => setFormData({ ...formData, defaultMaxWorkHours: parseInt(e.target.value) || 40 })}
                  placeholder="예: 40"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  근무자 선정 시 기본으로 설정될 최대 근로시간입니다.
                </Text>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">현재 학기로 설정</FormLabel>
                <Switch
                  isChecked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  colorScheme="green"
                />
              </FormControl>

              <Box p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.700">
                  <strong>안내:</strong> 학생들은 "시간표 입력 가능 기간" 내에만 자신의 학업시간표를 등록/수정할 수 있습니다.
                </Text>
              </Box>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              취소
            </Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={loading}>
              {editingSemester ? '수정' : '저장'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default SemesterManagement;
